// src/pages/Loads/LoadsInbox.tsx
/**
 * Timber Management System — Loads › Inbox
 *
 * What this page does
 * • Shows staged weighbridge rows waiting to be validated/mapped and posted
 * • Filters: search, status (All/New/Needs Mapping/Ready/Error/Posted), density toggle
 * • Saved Views: save/load/delete current filters (opts:inboxViews:v1)
 * • Bulk actions: Select visible, Validate, Mark Ready, Post (mock), Delete
 * • Per-row drawer to map Site, tweak weights, enter Product, etc.
 * • Validation: required fields, non-negative weights, net = gross - tare (if missing)
 * • Persists inbox rows in localStorage under `loads:inbox:v1`
 * • Audits + notifications for user feedback
 *
 * Assumptions
 * • AppShell, DataTable, Drawer, Skeleton, EmptyState, SavedViews exist per brief.
 * • Notifications via useNotifications() from @/lib/notifications.
 * • Roles from get('role','Admin'). Admin/Dispatcher can post/delete; others read-only.
 * • Sites list comes from fetchSites() (id + name at minimum).
 */

import React, { useEffect, useMemo, useState } from 'react'
import AppShell from '@/components/AppShell'
import DataTable, { Col } from '@/components/DataTable'
import Drawer from '@/components/Drawer'
import Skeleton from '@/components/Skeleton'
import EmptyState from '@/components/EmptyState'
import SavedViews from '@/components/SavedViews'
import { useNotifications } from '@/lib/notifications'
import { get, set } from '@/lib/storage'
import { logAudit } from '@/lib/audit'
import { fetchSites } from '@/lib/mockApi'
import {
  CheckSquare, Square, CheckCheck, Upload, Trash2, Info as InfoIcon,
  Download, Save
} from 'lucide-react'

type Role = 'Admin'|'Dispatcher'|'Loader'|'Trucker'|'Accountant'
const actorRole: Role = get<Role>('role', 'Admin' as Role)

type Status = 'new'|'mapping'|'ready'|'error'|'posted'
type InboxRow = {
  id: string
  importedAt: string // ISO
  source?: string
  status: Status
  // mapped / raw fields
  siteId?: string
  siteName?: string
  product?: string
  ticket?: string
  date?: string            // ISO
  truck?: string
  gross?: number
  tare?: number
  net?: number
  container?: string
  seal?: string
  voyage?: string
  port?: string
  notes?: string
  errors?: string[]
}

type Filters = {
  q: string
  status: 'all'|Status
  density: 'comfort'|'compact'
}

const INBOX_KEY   = 'loads:inbox:v1'
const VIEWS_KEY   = 'opts:inboxViews:v1'
const TABLE_KEY   = 'tbl:loads-inbox:v1'

function uid() { return (globalThis.crypto && 'randomUUID' in globalThis.crypto) ? (crypto as any).randomUUID() : Math.random().toString(36).slice(2) }
function fmtNum(n?: number) { return Number(n || 0).toLocaleString() }
function fmtDate(iso?: string) { return iso ? new Date(iso).toLocaleString() : '—' }
function cx(...xs: Array<string | false | null | undefined>) { return xs.filter(Boolean).join(' ') }

// ---------- validation ----------
function validateRow(r: InboxRow): InboxRow {
  const errs: string[] = []
  const req = (name: keyof InboxRow, label: string) => {
    const v = r[name]
    if (v === undefined || v === null || v === '') errs.push(`${label} required`)
  }
  req('ticket','Ticket')
  req('date','Date')
  if (!r.siteId) req('siteId', 'Site')
  if (!r.product) req('product', 'Product')
  const gross = Number(r.gross || 0)
  const tare  = Number(r.tare  || 0)
  let net     = r.net != null ? Number(r.net) : (gross - tare)
  if (!Number.isFinite(net)) net = 0
  if (net < 0) errs.push('Net weight negative')
  const next: InboxRow = { ...r, net, errors: errs }
  next.status = errs.length ? 'error' : (next.siteId && next.product ? 'ready' : 'mapping')
  return next
}

// ---------- page ----------
export default function LoadsInbox() {
  const { push } = useNotifications()

  const [rows, setRows] = useState<InboxRow[]>(() => get<InboxRow[]>(INBOX_KEY, []))
  const [sites, setSites] = useState<Array<{ id: string; name: string }>>([])
  const [loading, setLoading] = useState(false)

  const [filters, setFilters] = useState<Filters>(() => ({
    q: '',
    status: 'all',
    density: (get<'comfort'|'compact'>('ui:table:density','compact') || 'compact'),
  }))

  // selection + drawer
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<InboxRow | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    setLoading(true)
    fetchSites()
      .then((list) => setSites((list as any[]).map(s => ({ id: s.id, name: s.name }))))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { set('ui:table:density', filters.density) }, [filters.density])
  function persist(next: InboxRow[]) { setRows(next); set(INBOX_KEY, next) }

  // Seeding helper for quick testing
  function addSamples() {
    const now = Date.now()
    const make = (i: number): InboxRow => ({
      id: uid(),
      importedAt: new Date(now - i*3600_000).toISOString(),
      source: 'Weighbridge • Demo Mill',
      status: i % 4 === 0 ? 'mapping' : 'new',
      siteName: i % 3 === 0 ? 'North Ridge' : 'Pine Flats',
      product: i % 2 === 0 ? 'Pine Pulp' : '',
      ticket: `T-${1000+i}`,
      date: new Date(now - i*3600_000).toISOString(),
      truck: `TRK-${200+i}`,
      gross: 84_000 - i*250,
      tare:  32_000,
      net: undefined,
      container: i % 5 === 0 ? `C${9000+i}` : '',
      seal: i % 5 === 0 ? `S${7000+i}` : '',
      voyage: '', port: '',
      notes: '',
      errors: [],
    })
    const next = [make(1), make(2), make(3), make(4), make(5)].map(validateRow).concat(rows)
    persist(next)
    push({ title: 'Added sample rows', body: '5 demo inbox rows' })
    logAudit({ action: 'inbox:samples:add', count: 5 }, actorRole)
  }

  // filtering
  const visible = useMemo(() => {
    const term = (filters.q || '').trim().toLowerCase()
    return rows.filter((r) => {
      if (filters.status !== 'all' && r.status !== filters.status) return false
      if (!term) return true
      const hay = [
        r.ticket, r.truck, r.product, r.siteName, r.container, r.seal, r.voyage, r.port, r.notes, r.source
      ].filter(Boolean).join(' ').toLowerCase()
      return hay.includes(term)
    })
  }, [rows, filters])

  const counts = useMemo(() => {
    const base = { all: rows.length, new:0, mapping:0, ready:0, error:0, posted:0 }
    for (const r of rows) (base as any)[r.status]++
    return base
  }, [rows])

  // selection ops
  function toggleSelect(id: string) {
    setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })
  }
  function toggleSelectVisible() {
    setSelected(prev => {
      const next = new Set(prev)
      const ids = visible.map(v => v.id)
      const all = ids.length > 0 && ids.every(id => next.has(id))
      if (all) ids.forEach(id => next.delete(id)); else ids.forEach(id => next.add(id))
      return next
    })
  }
  const anySelected = selected.size > 0
  const canManage = actorRole === 'Admin' || actorRole === 'Dispatcher'

  // bulk actions
  function bulkValidate() {
    if (!anySelected) return
    const ids = new Set(selected)
    const next = rows.map(r => ids.has(r.id) ? validateRow(r) : r)
    persist(next)
    push({ title: 'Validated', body: `${ids.size} row(s)` })
    logAudit({ action: 'inbox:validate', count: ids.size }, actorRole)
  }
  function bulkMarkReady() {
    if (!anySelected) return
    const ids = new Set(selected)
    const next = rows.map(r => ids.has(r.id) ? validateRow({ ...r, status: 'ready' }) : r)
    persist(next)
    push({ title: 'Marked Ready', body: `${ids.size} row(s)` })
    logAudit({ action: 'inbox:markReady', count: ids.size }, actorRole)
  }
  function bulkPost() {
    if (!canManage || !anySelected) return
    const ids = new Set(selected)
    const updated: InboxRow[] = []
    let posted = 0
    for (const r of rows) {
      if (!ids.has(r.id)) { updated.push(r); continue }
      const v = validateRow(r)
      if (v.status === 'ready') {
        posted++
        updated.push({ ...v, status: 'posted' })
      } else {
        updated.push(v)
      }
    }
    persist(updated)
    setSelected(new Set())
    push({ title: 'Post complete', body: `${posted} posted, ${ids.size - posted} not ready`, level: posted ? 'info' : 'warning' })
    logAudit({ action: 'inbox:post', posted, attempted: ids.size }, actorRole)
  }
  function bulkDelete() {
    if (!canManage || !anySelected) return
    if (!confirm(`Delete ${selected.size} selected row(s)?`)) return
    const ids = new Set(selected)
    const next = rows.filter(r => !ids.has(r.id))
    persist(next)
    setSelected(new Set())
    push({ title: 'Deleted', body: `${ids.size} row(s)` })
    logAudit({ action: 'inbox:delete', count: ids.size }, actorRole)
  }

  // row edit
  function openEdit(row: InboxRow) { setEditing({ ...row }); setDrawerOpen(true) }
  function saveEdit() {
    if (!editing) return
    const next = rows.map(r => r.id === editing.id ? validateRow(editing) : r)
    persist(next)
    setDrawerOpen(false)
    push({ title: 'Row saved', body: editing.ticket || editing.id })
    logAudit({ action: 'inbox:row:save', id: editing.id }, actorRole)
  }

  // table columns
  const cols: Col[] = [
    { key: 'select',  label: '' },
    { key: 'ticket',  label: 'Ticket' },
    { key: 'date',    label: 'Date/Time' },
    { key: 'site',    label: 'Site' },
    { key: 'product', label: 'Product' },
    { key: 'truck',   label: 'Truck' },
    { key: 'net',     label: 'Net (lbs)' },
    { key: 'status',  label: 'Status' },
    { key: 'actions', label: 'Actions' },
  ]

  const rowsForTable = visible.map((r) => {
    const isSel = selected.has(r.id)
    const badgeClass =
      r.status === 'ready'   ? 'bg-emerald-100 text-emerald-800' :
      r.status === 'error'   ? 'bg-red-100 text-red-800' :
      r.status === 'mapping' ? 'bg-amber-100 text-amber-800' :
      r.status === 'posted'  ? 'bg-slate-200 text-slate-700' :
      'bg-slate-100 text-slate-700'

    return {
      select: (
        <div className="pl-2 min-w-[2.5rem]">
          <button className="btn-secondary px-2 py-1" aria-pressed={isSel} onClick={() => toggleSelect(r.id)}>
            {isSel ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
          </button>
        </div>
      ),
      ticket: (
        <div className="pl-3 pr-2 min-w-[10rem]">
          <div className="font-medium truncate" title={r.ticket || ''}>{r.ticket || '—'}</div>
          <div className="text-[11px] text-slate-500">Imported {fmtDate(r.importedAt)}</div>
        </div>
      ),
      date:   <div className="min-w-[12rem] tabular-nums">{fmtDate(r.date)}</div>,
      site:   <div className="min-w-[12rem] truncate" title={r.siteName || ''}>{r.siteName || '—'}</div>,
      product:<div className="min-w-[10rem] truncate" title={r.product || ''}>{r.product || '—'}</div>,
      truck:  <div className="min-w-[8rem] truncate" title={r.truck || ''}>{r.truck || '—'}</div>,
      net:    <div className="min-w-[8rem] text-right tabular-nums">{fmtNum(r.net)}</div>,
      status: <div className="min-w-[8rem]"><span className={cx('badge', badgeClass)}>{r.status}</span></div>,
      actions: (
        <div className="flex justify-end gap-2 pr-2 min-w-[10rem]">
          <button className="btn-secondary px-2 py-1" title="Info / Edit" onClick={() => openEdit(r)}>
            <InfoIcon className="w-4 h-4" />
          </button>
          {canManage && (
            <button className="btn-secondary px-2 py-1" title="Validate" onClick={() => { persist(rows.map(x => x.id===r.id ? validateRow(x) : x)); push({ title: 'Validated', body: r.ticket || r.id }) }}>
              <CheckCheck className="w-4 h-4" />
            </button>
          )}
          {canManage && (
            <button className="btn-danger px-2 py-1" title="Delete" onClick={() => { persist(rows.filter(x => x.id !== r.id)); push({ title: 'Deleted', body: r.ticket || r.id }); }}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    }
  })

  const densityClass = filters.density === 'compact' ? 'density-compact' : ''

  return (
    <AppShell title="Loads Inbox">
      {/* Toolbar */}
      <div className="card mb-3">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button className={filters.status === 'all' ? 'btn' : 'btn-secondary'} onClick={() => setFilters(f => ({ ...f, status: 'all' }))}>
              All <span className="badge ml-2">{counts.all}</span>
            </button>
            <button className={filters.status === 'new' ? 'btn' : 'btn-secondary'} onClick={() => setFilters(f => ({ ...f, status: 'new' }))}>
              New <span className="badge ml-2">{counts.new}</span>
            </button>
            <button className={filters.status === 'mapping' ? 'btn' : 'btn-secondary'} onClick={() => setFilters(f => ({ ...f, status: 'mapping' }))}>
              Needs Mapping <span className="badge ml-2">{counts.mapping}</span>
            </button>
            <button className={filters.status === 'ready' ? 'btn' : 'btn-secondary'} onClick={() => setFilters(f => ({ ...f, status: 'ready' }))}>
              Ready <span className="badge ml-2">{counts.ready}</span>
            </button>
            <button className={filters.status === 'error' ? 'btn' : 'btn-secondary'} onClick={() => setFilters(f => ({ ...f, status: 'error' }))}>
              Error <span className="badge ml-2">{counts.error}</span>
            </button>
            <button className={filters.status === 'posted' ? 'btn' : 'btn-secondary'} onClick={() => setFilters(f => ({ ...f, status: 'posted' }))}>
              Posted <span className="badge ml-2">{counts.posted}</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <SavedViews<Filters>
              storageKey={VIEWS_KEY}
              currentFilters={filters}
              onLoad={(next) => setFilters(next)}
            />
            <div className="popover relative">
              <details>
                <summary className="btn-secondary px-3 py-1.5">
                  Density: {filters.density === 'compact' ? 'Compact' : 'Comfort'}
                </summary>
                <div className="popover p-2 mt-2 w-48">
                  <button className="btn-secondary w-full mb-2" onClick={() => setFilters(f => ({ ...f, density: 'comfort' }))}>Comfort</button>
                  <button className="btn-secondary w-full"     onClick={() => setFilters(f => ({ ...f, density: 'compact' }))}>Compact</button>
                </div>
              </details>
            </div>
          </div>
        </div>

        <div className="card-body grid md:grid-cols-2 gap-3 items-start">
          <div>
            <label className="text-sm">Search</label>
            <input className="input" placeholder="Search ticket, site, truck, product…" value={filters.q} onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))} />
            <div className="text-xs muted mt-1">
              Showing <span className="tabular-nums">{visible.length.toLocaleString()}</span> row{visible.length === 1 ? '' : 's'}.
            </div>
          </div>

          <div className="flex md:justify-end items-end gap-2">
            {(() => {
              if (!anySelected) {
                return (
                  <button className="btn-secondary" onClick={addSamples} title="Seed demo rows">
                    <Download className="w-4 h-4" /> Add Samples
                  </button>
                )
              }
              return (
                <>
                  <button className="btn-secondary px-3 py-1.5" onClick={toggleSelectVisible}>
                    Select visible
                  </button>
                  <button className="btn-secondary" onClick={bulkValidate} title="Validate selected">
                    <CheckCheck className="w-4 h-4" /> Validate
                  </button>
                  <button className="btn-secondary" onClick={bulkMarkReady} title="Mark selected ready">
                    <Upload className="w-4 h-4" /> Mark Ready
                  </button>
                  {canManage && (
                    <>
                      <button className="btn" onClick={bulkPost} title="Post selected">
                        <Upload className="w-4 h-4" /> Post
                      </button>
                      <button className="btn-danger" onClick={bulkDelete} title="Delete selected">
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </>
                  )}
                </>
              )
            })()}
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <Skeleton className="h-64" />
      ) : rowsForTable.length === 0 ? (
        <EmptyState title="Inbox is empty">
          When the weighbridge poller runs, rows will land here. You can also{' '}
          <button className="underline" onClick={addSamples}>add sample rows</button>.
        </EmptyState>
      ) : (
        <div className={densityClass}>
          <DataTable columns={cols} rows={rowsForTable} filename="loads-inbox.csv" storageKey={TABLE_KEY} />
        </div>
      )}

      {/* Edit / Map Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing?.ticket ? `Ticket • ${editing.ticket}` : 'Inbox Row'}>
        {editing && (
          <div className="drawer-panel space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm">Site</label>
                <select
                  className="select w-full"
                  value={editing.siteId || ''}
                  onChange={(e) => {
                    const id = e.target.value || undefined
                    const name = id ? (sites.find(s => s.id === id)?.name || '') : ''
                    setEditing({ ...editing, siteId: id, siteName: name })
                  }}
                >
                  <option value="">— Select site —</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm">Product</label>
                <input className="input" placeholder="e.g., Pine Pulp" value={editing.product || ''} onChange={(e) => setEditing({ ...editing, product: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Ticket #</label>
                <input className="input" value={editing.ticket || ''} onChange={(e) => setEditing({ ...editing, ticket: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Date/Time</label>
                <input
                  type="datetime-local"
                  className="input"
                  value={editing.date ? toLocalInput(editing.date) : ''}
                  onChange={(e) => setEditing({ ...editing, date: fromLocalInput(e.target.value) })}
                />
              </div>
              <div>
                <label className="text-sm">Truck</label>
                <input className="input" value={editing.truck || ''} onChange={(e) => setEditing({ ...editing, truck: e.target.value })} />
              </div>
              <div />
              <div>
                <label className="text-sm">Gross (lbs)</label>
                <input type="number" className="input" value={editing.gross ?? 0} onChange={(e) => setEditing({ ...editing, gross: Number(e.target.value || 0) })} />
              </div>
              <div>
                <label className="text-sm">Tare (lbs)</label>
                <input type="number" className="input" value={editing.tare ?? 0} onChange={(e) => setEditing({ ...editing, tare: Number(e.target.value || 0) })} />
              </div>
              <div>
                <label className="text-sm">Net (lbs)</label>
                <input
                  type="number"
                  className="input"
                  value={editing.net ?? 0}
                  onChange={(e) => setEditing({ ...editing, net: Number(e.target.value || 0) })}
                />
                <div className="text-xs mt-1">
                  Tip: leave blank to auto-calc from Gross − Tare.
                </div>
              </div>
              <div />
              <div>
                <label className="text-sm">Container #</label>
                <input className="input" value={editing.container || ''} onChange={(e) => setEditing({ ...editing, container: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Seal #</label>
                <input className="input" value={editing.seal || ''} onChange={(e) => setEditing({ ...editing, seal: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Voyage</label>
                <input className="input" value={editing.voyage || ''} onChange={(e) => setEditing({ ...editing, voyage: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Port</label>
                <input className="input" value={editing.port || ''} onChange={(e) => setEditing({ ...editing, port: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm">Notes</label>
                <input className="input" value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
              {editing.errors && editing.errors.length > 0 && (
                <div className="sm:col-span-2">
                  <div className="text-sm mb-1">Validation Errors</div>
                  <ul className="list-disc pl-5 text-sm text-red-700">
                    {editing.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <button className="btn-secondary" onClick={() => setDrawerOpen(false)}>Close</button>
              <div className="flex-1" />
              <button className="btn-secondary" onClick={() => setEditing(e => e ? validateRow(e) : e)} title="Validate now">
                <CheckCheck className="w-4 h-4" /> Validate
              </button>
              <button className="btn" onClick={saveEdit}>
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          </div>
        )}
      </Drawer>
    </AppShell>
  )
}

/* ---------- helpers ---------- */
function toLocalInput(iso: string) {
  // convert ISO -> yyyy-MM-ddTHH:mm for input[type=datetime-local]
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2,'0')
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth()+1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const mi = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}
function fromLocalInput(v: string) {
  if (!v) return ''
  // v is local datetime without timezone; treat as local and convert to ISO
  const d = new Date(v)
  return d.toISOString()
}
